/**
 * Catálogo de templates de comunicación de Habitia.
 *
 * Cada template corresponde a un caso de uso operativo (cobranza por etapa,
 * recordatorio de gasto común, aviso de incidencia, etc.). Los que tienen
 * canal "whatsapp" requieren aprobación de Meta vía Meta Business Manager.
 *
 * Estados Meta posibles:
 *  - PENDING_SUBMIT: el copy está armado pero todavía no se mandó a Meta.
 *  - PENDING_REVIEW: enviado a Meta, esperando aprobación (24-72h típico).
 *  - APPROVED: aprobado, listo para uso outbound sin ventana 24h.
 *  - REJECTED: Meta rechazó, hay que ajustar copy y reenviar.
 *  - PAUSED: aprobado pero deshabilitado a nivel plataforma.
 *
 * Para que un edificio use un template, el admin lo activa desde la tab
 * "Comunicaciones" de Editar Edificio. Se guarda en
 * `building/{id}.communicationTemplates: { [templateKey]: { enabled, updated_at, updated_by } }`.
 *
 * NOTA: este catálogo es la fuente de verdad inicial. Cuando exista la
 * pantalla SuperAdmin de gestión, los templates se moverán a la colección
 * `notificationTemplates` de Firestore (ya existe el seed en
 * `functions/scripts/seed-notification-templates.js`). Hasta entonces, este
 * archivo es lo que ven los admins.
 */

export const COMMUNICATION_TEMPLATES = [
  // ────────────────────────────────────────────────────────────────────────
  // COBRANZA — una entrada por etapa (4 etapas activas; etapa 5 va por
  // carta-documento, no por WhatsApp).
  // ────────────────────────────────────────────────────────────────────────
  {
    key: "cobranza_etapa_1",
    metaTemplateName: "cobranza_etapa_1",
    category: "cobranza",
    stage: 1,
    label: "Cobranza — Etapa 1 (recordatorio amable)",
    description: "Primer recordatorio de pago vencido 1-7 días. Tono cercano, asume despiste.",
    channel: "whatsapp",
    metaCategory: "UTILITY",
    metaStatus: "PENDING_SUBMIT",
    language: "es",
    body:
      "Hola {{1}}, te recordamos desde la administración de {{2}} que tu gasto común de {{3}} venció el {{4}}.\n\n" +
      "Saldo pendiente: ${{5}}.\n\n" +
      "Si ya lo abonaste, ignorá este mensaje. Si necesitás coordinar, respondé este mensaje.",
    variables: [
      { idx: 1, name: "nombre_residente", source: "users.NombreCompleto" },
      { idx: 2, name: "nombre_edificio", source: "building.name" },
      { idx: 3, name: "periodo", source: "payment.month_label" },
      { idx: 4, name: "fecha_vencimiento", source: "payment.due_date_uy" },
      { idx: 5, name: "monto_total", source: "payment.remaining_amount_uy" },
    ],
    // Opt-in, igual que el backend (sendCollectionMessage exige enabled === true):
    // antes la UI la mostraba encendida y el envío la rechazaba.
    defaultEnabled: false,
  },
  {
    key: "cobranza_etapa_2",
    metaTemplateName: "cobranza_etapa_2",
    category: "cobranza",
    stage: 2,
    label: "Cobranza — Etapa 2 (recordatorio firme)",
    description: "Recordatorio firme pero cordial para 8-30 días vencidos. Incluye monto exacto.",
    channel: "whatsapp",
    metaCategory: "UTILITY",
    metaStatus: "PENDING_SUBMIT",
    language: "es",
    body:
      "Hola {{1}}, te escribimos desde la administración de {{2}}.\n\n" +
      "Registramos un saldo pendiente de ${{3}} correspondiente a {{4}} (vencido hace {{5}} días).\n\n" +
      "Para evitar recargos, te pedimos regularizarlo en los próximos días. " +
      "Podés confirmarnos el pago o coordinar un plan respondiendo a este mensaje.",
    variables: [
      { idx: 1, name: "nombre_residente", source: "users.NombreCompleto" },
      { idx: 2, name: "nombre_edificio", source: "building.name" },
      { idx: 3, name: "monto_total", source: "payment.remaining_amount_uy" },
      { idx: 4, name: "periodo", source: "payment.month_label" },
      { idx: 5, name: "dias_vencidos", source: "payment.days_overdue" },
    ],
    defaultEnabled: false, // opt-in (ver etapa 1)
  },
  {
    key: "cobranza_etapa_3",
    metaTemplateName: "cobranza_etapa_3",
    category: "cobranza",
    stage: 3,
    label: "Cobranza — Etapa 3 (aviso formal)",
    description: "Aviso formal para 31-60 días vencidos. Menciona próxima intimación. Requiere confirmación humana antes de enviar.",
    channel: "whatsapp",
    metaCategory: "UTILITY",
    metaStatus: "PENDING_SUBMIT",
    language: "es",
    body:
      "Estimado/a {{1}}, le escribimos desde la administración de {{2}}.\n\n" +
      "Su saldo de ${{3}} ({{4}}) lleva {{5}} días vencido. " +
      "De no regularizarse en los próximos 10 días, deberemos iniciar gestiones formales de cobro.\n\n" +
      "Le pedimos contactarnos para coordinar el pago o un plan respondiendo a este mensaje.",
    variables: [
      { idx: 1, name: "nombre_residente", source: "users.NombreCompleto" },
      { idx: 2, name: "nombre_edificio", source: "building.name" },
      { idx: 3, name: "monto_total", source: "payment.remaining_amount_uy" },
      { idx: 4, name: "periodo", source: "payment.month_label" },
      { idx: 5, name: "dias_vencidos", source: "payment.days_overdue" },
    ],
    requiresHumanConfirmation: true,
    defaultEnabled: false,
  },
  {
    key: "cobranza_etapa_4",
    metaTemplateName: "cobranza_etapa_4",
    category: "cobranza",
    stage: 4,
    label: "Cobranza — Etapa 4 (antesala legal)",
    description: "Último aviso antes de carta-documento. 61-90 días vencidos. Requiere confirmación humana.",
    channel: "whatsapp",
    metaCategory: "UTILITY",
    metaStatus: "PENDING_SUBMIT",
    language: "es",
    body:
      "Estimado/a {{1}}, le notificamos desde la administración de {{2}} que su deuda de ${{3}} " +
      "lleva más de {{4}} días vencida.\n\n" +
      "Este es un último aviso previo a la emisión de carta-documento. " +
      "Le pedimos contactarnos en las próximas 72 horas para regularizar la situación.",
    variables: [
      { idx: 1, name: "nombre_residente", source: "users.NombreCompleto" },
      { idx: 2, name: "nombre_edificio", source: "building.name" },
      { idx: 3, name: "monto_total", source: "payment.remaining_amount_uy" },
      { idx: 4, name: "dias_vencidos", source: "payment.days_overdue" },
    ],
    requiresHumanConfirmation: true,
    defaultEnabled: false,
  },
];

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

/**
 * Devuelve los templates agrupados por categoría (cobranza, recordatorios, etc.).
 */
export function groupTemplatesByCategory(templates = COMMUNICATION_TEMPLATES) {
  const groups = {};
  for (const tpl of templates) {
    if (!groups[tpl.category]) groups[tpl.category] = [];
    groups[tpl.category].push(tpl);
  }
  return groups;
}

export const CATEGORY_LABELS = {
  cobranza: "Cobranza",
  recordatorios: "Recordatorios",
  incidencias: "Incidencias",
  novedades: "Novedades",
};

export const META_STATUS_LABELS = {
  PENDING_SUBMIT: { label: "Pendiente de envío a Meta", color: "warning" },
  PENDING_REVIEW: { label: "En revisión por Meta", color: "info" },
  APPROVED: { label: "Aprobado", color: "success" },
  REJECTED: { label: "Rechazado", color: "error" },
  PAUSED: { label: "Pausado", color: "gray" },
};

/**
 * Indica si un template está habilitado para un edificio.
 *
 * Orden de resolución (espeja el enforcement real del backend, 24-sep-2026):
 *   1. Override del edificio (building.communicationTemplates[key].enabled).
 *   2. defaultEnabled del catálogo, si está definido (solo cobranza lo trae).
 *   3. Default por categoría: cobranza es OPT-IN (sendCollectionMessage exige
 *      override habilitado); el resto de los avisos automáticos son OPT-OUT
 *      (notifyUser los manda salvo override en false). Antes este fallback
 *      devolvía false para todo → la UI mostraba "Deshabilitado" en plantillas
 *      que el sistema mandaba igual.
 */
export function isTemplateEnabledForBuilding(template, buildingData) {
  const overrides = buildingData?.communicationTemplates || {};
  const override = overrides[template.key];
  if (override && typeof override.enabled === "boolean") {
    return override.enabled;
  }
  if (typeof template.defaultEnabled === "boolean") {
    return template.defaultEnabled;
  }
  return template.category !== "cobranza";
}

/**
 * true si el estado mostrado sale de un override guardado para el edificio;
 * false si es el comportamiento por defecto (la UI lo aclara con "por defecto").
 */
export function hasBuildingOverride(template, buildingData) {
  const override = (buildingData?.communicationTemplates || {})[template.key];
  return Boolean(override && typeof override.enabled === "boolean");
}
